"use client";

import { useEffect, useState } from "react";
import { useAdmin } from "@/lib/admin/context";
import { Icon } from "@/lib/icons/Icon";
import { FormStatus } from "@/components/ui/Form";
import {
  CHROME_AREAS,
  isLayoutSymbol,
  type AreaSymbol,
} from "@/lib/layouts/types";
import type { CmsLayout } from "@/lib/api/cms-layouts";
import {
  DRAG_FROM_AREA,
  DRAG_FROM_PANEL,
  DRAG_MIME,
  parseDragPayload,
} from "@/lib/admin/dnd/dragPayload";
import type { UnpublishedWindow } from "@/lib/windows/types";
import { confirmDialog } from "@/lib/notifications/confirmDialog";
import { toast } from "@/lib/notifications/toast";
import { saveAsDefaultAction } from "@/lib/layouts/template-actions";
import { resetToDefaultsAction } from "@/lib/areas/actions";
import { deleteWindowAction } from "@/lib/windows/actions";
import { iadminUrl, buildIadminFullUrl } from "@/lib/admin/iadmin-links";
import styles from "./LayoutAndWindowsPanel.module.css";

/**
 * LayoutAndWindowsPanel — drop-down panel pod top barem, **full-width**,
 * 3-kolumnowy układ (layout | areas | unpublished windows).
 *
 * Renderowany TYLKO gdy `activeTab === 'layoutAndWindows'`.
 *
 * **Zamykany WYŁĄCZNIE przez przycisk X (close button).** Brak outside-click
 * handler'a, brak Escape handler'a, brak toggle przez ponowny klik na taba.
 * Decyzja UX: panel admin to workspace — user explicit dismiss zapobiega
 * przypadkowemu zamykaniu w trakcie pracy (np. drag z panelu poza top bar
 * podczas DnD okien, klik w stronę żeby zobaczyć efekt zmiany layoutu, focus
 * w pole search wewnątrz panelu).
 *
 * Faza G MVP (now): mock data inline. Real data w Fazie F.2+ z REST API:
 *   - `GET /IRESTAPI/v1/IIMP_CMS_LAYOUT_CONTROLLER/listForDomain`
 *   - `GET /IRESTAPI/v1/IIMP_CMS_CATALOG_WINDOW_CONTROLLER/areasForLayout/{id}`
 *   - `GET /IRESTAPI/v1/IIMP_WINDOW_CONTROLLER/unpublished`
 */
export function LayoutAndWindowsPanel() {
  const {
    setActiveTab,
    currentPageType,
    currentLayout,
    setLayoutForCurrentPage,
    windowsState,
    removeWindowFromArea,
    commitWindowsState,
    disabledAreas,
    commitAreaStateChanges,
    windowsSource,
    previewMode,
    togglePreviewMode,
    previewData,
    canModify,
    canDelete,
    setDraggedWindowId,
    pageWindows,
    unpublishedWindows,
    selectedWindowId,
    setSelectedWindowId,
    availableLayouts: allAvailableLayouts,
    adminPanelUrl,
    layoutSource,
    defaultTemplateExists,
    activeTemplate,
    locale,
    currentSlug,
    currentArticleId,
    currentIdCatalog,
    saveScope,
    setSaveScope,
    withPendingRequest,
    t,
  } = useAdmin();
  const [searchQuery, setSearchQuery] = useState("");
  /* Filtr typu komponentu — pusty string = "Wszystkie typy". Discriminator
     = `componentSymbol` (np. `w_menu`). Label dropdown'a bierzemy z
     `componentName[locale]` (PL fallback), tak żeby user widział czytelną
     nazwę typu zamiast snake_case symbolu. */
  const [componentFilter, setComponentFilter] = useState<string>("");
  /* Per-sekcja collapse — preferencja persistowana w localStorage żeby przeżyła
     refresh. Wzorzec jak `cms_dev_banner_hidden` w AdminContext. Trzy niezależne
     toggle'e (layout management, biblioteka okien, układ strony) — każdy z
     własnym storage-key, można niezależnie zwijać sekcje żeby skoncentrować
     widok na tym co potrzebne. */
  const [layoutMgmtCollapsed, setLayoutMgmtCollapsed] = usePersistedCollapse(
    "cms_layout_mgmt_collapsed",
  );
  const [libraryCollapsed, setLibraryCollapsed] = usePersistedCollapse(
    "cms_library_collapsed",
  );
  const [pageLayoutCollapsed, setPageLayoutCollapsed] = usePersistedCollapse(
    "cms_page_layout_collapsed",
  );
  /* IDs okien obecnie opublikowanych w draftState LayoutMap'y. Reverse-data-flow
     przez callback `onDraftPublishedIdsChange` — LayoutMap notyfikuje gdy admin
     przeciągnie okno do/z obszaru. Używamy do filtrowania "Biblioteki okien"
     (nieopublikowane) żeby okno znikało z listy NATYCHMIAST po drop'ie, nie
     dopiero po save (gdy backend revalidate zwróci updated unpublishedWindows). */
  const [draftPublishedIds, setDraftPublishedIds] = useState<Set<number>>(new Set());
  /* `selectedWindowId` lifted do AdminContext — globalny dev banner
     (SelectedWindowDevBanner w PublicLayout) reaguje na zmianę selekcji.
     Klik na tile toggle'uje wartość w context'cie. */

  /* Per-page-type layouts pool — z context.availableLayouts (backend fetched
     server-side). Filtrujemy po `supports_homepage`/`supports_subpage` per
     bieżący page type. Pusta lista = admin nie przypisał layoutów w panelu
     IADMIN (banner developera "BRAK LAYOUTÓW DLA DOMENY" już to zgłasza). */
  const availableLayouts: CmsLayout[] = allAvailableLayouts.filter((l) =>
    currentPageType === "catalog" ? l.supports_subpage : l.supports_homepage,
  );
  /* `selectedMeta` — wybrany layout w aktualnym widoku. Resolve order:
     1. layout o symbolu === currentLayout (z context)
     2. fallback: pierwszy z dostępnych dla page type
     3. null gdy lista pusta (UI pokazuje pusty select) */
  const selectedMeta: CmsLayout | null =
    availableLayouts.find((l) => l.symbol === currentLayout) ??
    availableLayouts[0] ??
    null;
  /* `layoutMismatch` — obowiązujący symbol NIE należy do puli układów tego typu
     strony (np. katalogowy `LCatalogSingleColumn` zapisany na stronie głównej).
     Fallback `availableLayouts[0]` powyżej zostaje, bo mapa obszarów musi się
     z czegoś wyrenderować — ale UI ma o tym POWIEDZIEĆ wprost. Bez tego select
     bez pasującego `value` renderował pierwszą opcję listy i admin czytał nazwę
     układu, którego nigdzie nie zapisano, a okna przypisane do obszarów spoza
     zapisanego układu po prostu nie pojawiały się na stronie. */
  const layoutMismatch =
    availableLayouts.length > 0 &&
    !availableLayouts.some((l) => l.symbol === currentLayout);

  const handleLayoutChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    if (!isLayoutSymbol(next)) return;
    if (next === currentLayout) return;

    /* Walidacja: czy `next` jest w puli backend-fetched dla aktualnego page type. */
    const nextMeta = availableLayouts.find((l) => l.symbol === next);
    if (!nextMeta) return;
    if (!selectedMeta) {
      setLayoutForCurrentPage(next);
      return;
    }

    /* Wykrywamy areas które ZNIKNĄ po zmianie layoutu. */
    const currentAreas = new Set(selectedMeta.areas.map((a) => a.area_key));
    const nextAreas = new Set(nextMeta.areas.map((a) => a.area_key));
    const disappearingAreas = [...currentAreas].filter(
      (a) => !nextAreas.has(a),
    ) as AreaSymbol[];

    /* Filtruj tylko te które MAJĄ okna — areas bez windows nie wymagają potwierdzenia */
    const affectedAreas = disappearingAreas.filter(
      (area) => (windowsState.publishedByArea[area]?.length ?? 0) > 0,
    );

    if (affectedAreas.length > 0) {
      const totalWindows = affectedAreas.reduce(
        (sum, area) => sum + (windowsState.publishedByArea[area]?.length ?? 0),
        0,
      );
      const areaNames = affectedAreas
        .map((symbol) => {
          const a = selectedMeta.areas.find((x) => x.area_key === symbol);
          return a ? pickLocaleLabel(a.name, a.area_key) : symbol;
        })
        .join(", ");

      const ok = await confirmDialog({
        title: t.layoutPanel.changeLayoutDialogTitle,
        message: t.layoutPanel.changeLayoutDialogMessage(totalWindows, affectedAreas.length, areaNames),
        variant: "warning",
        confirmLabel: t.layoutPanel.changeLayoutConfirmLabel,
        cancelLabel: t.layoutPanel.cancelLabel,
      });

      if (!ok) return;

      /* Usuwamy okna z każdego znikającego area — context używa stateRef,
         sequential calls są bezpieczne (każdy widzi nową wartość po poprzednim). */
      for (const area of affectedAreas) {
        const ids = windowsState.publishedByArea[area] ?? [];
        for (const id of ids) {
          removeWindowFromArea(id, area);
        }
      }

      toast.success(t.layoutPanel.layoutChangedReturnedToast(totalWindows));
    } else {
      toast.success(t.layoutPanel.layoutChangedToast(pickLocaleLabel(nextMeta.name, nextMeta.symbol)));
    }

    setLayoutForCurrentPage(next);
  };

  /* `unpublishedWindows` z AdminContext jest już strict draftami (okna nie
     przypisane do ŻADNEJ strony przez `imp_cms_catalog_window`). Source:
     `GET /cms/windows/unpublished` z `includePublished=false`. Visitor view
     używa odrębnego pola `pageWindows` — patrz `lib/admin/types.ts`. */

  /* Lista unikalnych typów komponentów wśród aktualnie nieopublikowanych okien —
     źródło opcji dla dropdown'a "Typ komponentu". Każdy `componentSymbol` raz,
     label = `componentName[pl]` (admin UI w PL) z fallback do symbolu. Sortujemy
     alfabetycznie po labelu. */
  const componentTypeOptions: Array<{ symbol: string; label: string }> = (() => {
    const seen = new Map<string, string>();
    for (const w of unpublishedWindows) {
      if (!seen.has(w.componentSymbol)) {
        seen.set(w.componentSymbol, pickLocaleLabel(w.componentName, w.componentSymbol));
      }
    }
    return [...seen.entries()]
      .map(([symbol, label]) => ({ symbol, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pl"));
  })();

  /* Lista okien po aplikacji filtrów: typ komponentu (dropdown) + fraza
     (input search) + DRAFT EXCLUSION (okno przeciągnięte do obszaru znika
     z biblioteki od razu, bez czekania na save -> revalidate -> fresh fetch).
     Search dopasowuje zarówno do nazwy instancji, jak i do labelu typu
     komponentu — admin może szukać po obu. */
  const filteredUnpublishedWindows = unpublishedWindows.filter((w) => {
    if (draftPublishedIds.has(w.id)) return false;
    if (componentFilter !== "" && w.componentSymbol !== componentFilter) return false;
    if (searchQuery === "") return true;
    const needle = searchQuery.toLowerCase();
    const componentLabel = pickLocaleLabel(w.componentName, w.componentSymbol);
    return (
      w.name.toLowerCase().includes(needle) ||
      componentLabel.toLowerCase().includes(needle)
    );
  });

  const handleWindowDragStart = (e: React.DragEvent<HTMLLIElement>, windowId: number) => {
    const payload = JSON.stringify({ type: DRAG_FROM_PANEL, windowId });
    e.dataTransfer.setData(DRAG_MIME, payload);
    e.dataTransfer.effectAllowed = "move";
    setDraggedWindowId(windowId); //informuj wszystkie WindowArea o tym co ciągniemy
  };

  const handleWindowDragEnd = () => {
    setDraggedWindowId(null);
  };

  /* Close X — zamykanie panelu wyzeruje `activeTab`. Panel widoczny gdy
     `activeTab === 'layoutAndWindows'`, więc `setActiveTab(null)` go ukrywa.
     Visit page zawsze pozostaje w trybie visitor (po unifikacji renderowania). */
  const handleClose = () => {
    setActiveTab(null);
  };

  /* "Zapisz domyślny" — promote bieżącej konfiguracji (currentLayout + windowsState)
     do global default templatu w bazie. Confirm gdy default already exists. */
  const handleSaveAsDefault = async () => {
    if (currentPageType !== "catalog") {
      toast.error(t.layoutPanel.defaultLayoutOnlySubpageError);
      return;
    }
    if (!selectedMeta) {
      toast.error(t.layoutPanel.pickLayoutFirstError);
      return;
    }
    const message = defaultTemplateExists
      ? t.layoutPanel.overwriteDialogMessageFull
      : t.layoutPanel.saveDefaultDialogMessageFull;
    const title = defaultTemplateExists
      ? t.layoutPanel.overwriteDialogTitle
      : t.layoutPanel.saveAsDefaultDialogTitle;
    const variant: "warning" | "info" = defaultTemplateExists ? "warning" : "info";
    const ok = await confirmDialog({
      title,
      message,
      variant,
      confirmLabel: defaultTemplateExists ? t.layoutPanel.overwriteConfirmLabel : t.layoutPanel.saveAsDefaultConfirmLabel,
      cancelLabel: t.layoutPanel.cancelLabel,
    });
    if (!ok) return;

    await withPendingRequest(async () => {
      /* Mapowanie publishedByArea -> windows_by_area dla backend body.
         Filtrujemy `undefined` z Partial map. */
      const windowsByArea: Record<string, number[]> = {};
      for (const [area, ids] of Object.entries(windowsState.publishedByArea)) {
        if (ids && ids.length > 0) windowsByArea[area] = ids;
      }
      const result = await saveAsDefaultAction({
        layout: currentLayout,
        windowsByArea,
        lang: locale,
      });
      if (!result.ok) {
        toast.error(t.layoutPanel.saveDefaultErrorToast(result.error ?? t.contextToasts.unknownError));
        return;
      }
      toast.success(
        result.wasUpdate
          ? t.layoutPanel.overwriteDefaultSuccessToast(result.windowsInserted ?? 0)
          : t.layoutPanel.saveDefaultSuccessToast(result.windowsInserted ?? 0),
      );
    });
  };

  /* Reset to defaults — pełny wipe per-page wpisów (layouty + okna + area state)
     dla bieżącego id_catalog. Po wipe cascade fallback'uje do template_default.
     Widoczny TYLKO gdy `defaultTemplateExists=true` (jest co fallbackować). */
  const handleResetToDefaults = async () => {
    const ok = await confirmDialog({
      title: t.layoutPanel.resetToDefaultsDialogTitle,
      message: t.layoutPanel.resetToDefaultsDialogMessage,
      variant: "danger",
      confirmLabel: t.layoutPanel.resetToDefaultsConfirmLabel,
      cancelLabel: t.layoutPanel.cancelLabel,
    });
    if (!ok) return;

    await withPendingRequest(async () => {
      const result = await resetToDefaultsAction({
        idCatalog: currentIdCatalog,
        lang: locale,
        slug: currentSlug,
      });
      if (!result.ok) {
        toast.error(t.layoutPanel.resetToDefaultsErrorToast(result.error ?? t.contextToasts.unknownError));
        return;
      }
      toast.success(t.layoutPanel.resetToDefaultsSuccessToast(result.totalDeleted ?? 0));
      /* Pełen browser reload zamiast router.refresh — reset kasuje 3 tabele
         backendowe (layouty + okna + area state) dla całego id_catalog
         (type=1/2/3), więc LayoutMap'a draft state + AdminContext muszą być
         re-fetched from scratch. `router.refresh` re-rendrowałby server
         components ale local component state (np. `draftState` w LayoutMap)
         pozostałby z poprzednimi okneami -> desync z backendem. Reload =
         pewny clean slate (wzorzec jak logout w UserMenuDropdown). */
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    });
  };

  return (
    <aside className={styles.panel} aria-label={t.layoutPanel.panelAriaLabel}>
      <button
        type="button"
        className={styles.closeBtn}
        onClick={handleClose}
        aria-label={t.layoutPanel.closeAria}
        title={t.layoutPanel.closePanelTitle}
      >
        <Icon name="x" />
      </button>

      <div className={styles.body}>
        {/* Preview mode banner — widoczny gdy admin włączył podgląd układu
            domyślnego. Edycja w panelu jest zablokowana, banner zawiera
            quick-action "Wyłącz podgląd". */}
        {previewMode && (
          <div className={`${styles.stateBanner} ${styles.previewModeBanner}`}>
            <Icon name="info" className={styles.stateBannerIcon} />
            <span className={styles.previewModeBannerText}>
              <strong>{t.layoutPanel.previewModeBannerTitle}</strong>
              {" — "}
              {t.layoutPanel.previewModeBannerHint}
            </span>
            <button
              type="button"
              className={styles.previewModeBannerBtn}
              onClick={togglePreviewMode}
            >
              <Icon name="x" />
              <span>{t.layoutPanel.previewModeExitLabel}</span>
            </button>
          </div>
        )}
        <div className={styles.columns}>
          {/* KOLUMNA 1: Zarządzanie układem strony — wewnątrz 2-kolumnowy grid:
                LEWA  -> wybór layoutu (dropdown + hint) — kluczowy input
                PRAWA -> kontekstowe akcje (saveScope radio, bannery stanu,
                        przyciski "Nadpisz domyślny" + "Przywróć ustawienia
                        domyślne"). Na homepage prawa kolumna pusta —
                        `columnInnerGridSingle` resetuje do 1-kolumny. */}
          <section className={styles.column}>
            <button
              type="button"
              className={styles.sectionToggle}
              onClick={() => setLayoutMgmtCollapsed(!layoutMgmtCollapsed)}
              aria-expanded={!layoutMgmtCollapsed}
              aria-controls="cms-layout-mgmt-body"
              title={
                layoutMgmtCollapsed
                  ? t.layoutPanel.sectionExpandTitle
                  : t.layoutPanel.sectionCollapseTitle
              }
            >
              <Icon
                name={layoutMgmtCollapsed ? "caret-down" : "caret-up"}
                className={styles.sectionToggleIcon}
              />
              <h3 className={styles.colTitle}>{t.layoutPanel.columnHeading}</h3>
            </button>
            {!layoutMgmtCollapsed && (
            <div id="cms-layout-mgmt-body">
            {(() => {
              const hasRightColumn = currentPageType !== "homepage";
              const hasCustomConfig =
                layoutSource === "catalog" ||
                windowsSource === "catalog" ||
                disabledAreas.length > 0;
              return (
                <div
                  className={
                    hasRightColumn
                      ? styles.columnInnerGrid
                      : `${styles.columnInnerGrid} ${styles.columnInnerGridSingle}`
                  }
                >
                  {/* LEWA KOLUMNA — wybór layoutu */}
                  <div className={styles.columnInnerCell}>
                    <div className={styles.layoutSelectRow}>
                      <label className={styles.colSubLabel}>
                        {currentPageType === "catalog" ? t.layoutPanel.subpageLayoutHeading : t.layoutPanel.homepageLayoutHeading}
                      </label>
                      <select
                        className={styles.select}
                        value={layoutMismatch ? currentLayout : (selectedMeta?.symbol ?? "")}
                        onChange={handleLayoutChange}
                        disabled={availableLayouts.length === 0 || previewMode || !canModify}
                      >
                        {availableLayouts.length === 0 && (
                          <option value="" disabled>
                            {activeTemplate
                              ? t.layoutPanel.noLayoutsForTemplatePlaceholder(activeTemplate)
                              : t.layoutPanel.noTemplateSetPlaceholder}
                          </option>
                        )}
                        {/* Syntetyczna opcja dla symbolu spoza puli — `disabled`,
                            bo nie jest wyborem do przywrócenia, tylko informacją
                            co faktycznie obowiązuje. */}
                        {layoutMismatch && (
                          <option value={currentLayout} disabled>
                            {t.layoutPanel.mismatchOptionLabel(currentLayout)}
                          </option>
                        )}
                        {availableLayouts.map((l) => (
                          <option key={l.symbol} value={l.symbol}>
                            {pickLocaleLabel(l.name, l.symbol)}
                          </option>
                        ))}
                      </select>
                    </div>
                    {layoutMismatch && selectedMeta !== null && (
                      <div className={`${styles.stateBanner} ${styles.stateBannerWarning}`}>
                        <Icon name="warning-circle" className={styles.stateBannerIcon} />
                        <span>
                          {t.layoutPanel.mismatchBanner(
                            currentLayout,
                            pickLocaleLabel(selectedMeta.name, selectedMeta.symbol),
                          )}
                        </span>
                      </div>
                    )}
                    <p className={styles.colHint}>
                      {currentPageType === "catalog"
                        ? t.layoutPanel.helpTextSubpage
                        : t.layoutPanel.helpTextHomepage}
                      {" "}{t.layoutPanel.layoutChangeReloadHint}
                    </p>
                  </div>

                  {/* PRAWA KOLUMNA — kontekstowe akcje (article scope, bannery,
                      przyciski). Renderuje się tylko na subpages — homepage
                      ma `columnInnerGridSingle` -> grid template = 1fr i ta
                      część jest nieobecna. */}
                  {hasRightColumn && (
                    <div className={styles.columnInnerCell}>
                      {/* SaveScope radio — widoczny dla każdego nie-homepage view
                          (catalog index + article). Hierarchia priorytetów (od
                          najwyższego): Adres (type=3) > Artykuły (type=2) >
                          Katalog (type=1). Zapis niższego priorytetu cascade-
                          kasuje wyższe (backend `cascadeWipeHigherPriorityScopes`).

                          **Disable rule:** opcje "Adres" i "Artykuły" wymagają
                          żebyśmy byli na URL-u różnym od głównej strony katalogu
                          (czyli `currentArticleId !== null`). Na catalog index
                          tylko "Katalog" ma sens — pozostałe disabled z tooltipem. */}
                      {(() => {
                        const onCatalogIndex = currentArticleId === null;
                        const narrowerDisabled = onCatalogIndex || previewMode || !canModify;
                        const narrowerTooltip = onCatalogIndex
                          ? t.layoutPanel.saveScopeNarrowerDisabledOnCatalogIndex
                          : previewMode
                            ? t.layoutPanel.editLockedInPreviewTooltip
                            : !canModify
                              ? t.layoutPanel.editLockedNoPermissionTooltip
                              : undefined;
                        return (
                        <fieldset className={styles.scopeFieldset}>
                          <legend className={styles.scopeLegend}>{t.layoutPanel.saveScopeHeading}</legend>
                          <label className={styles.scopeOption} title={narrowerTooltip}>
                            <input
                              type="radio"
                              name="saveScope"
                              value="this_url"
                              checked={saveScope === "this_url"}
                              onChange={() => setSaveScope("this_url")}
                              disabled={narrowerDisabled}
                            />
                            <span className={styles.scopeOptionLabel}>{t.layoutPanel.saveScopeThisUrl}</span>
                            <span className={styles.scopeOptionHint}>{t.layoutPanel.saveScopeThisUrlHint}</span>
                          </label>
                          <label className={styles.scopeOption} title={narrowerTooltip}>
                            <input
                              type="radio"
                              name="saveScope"
                              value="all_articles"
                              checked={saveScope === "all_articles"}
                              onChange={() => setSaveScope("all_articles")}
                              disabled={narrowerDisabled}
                            />
                            <span className={styles.scopeOptionLabel}>{t.layoutPanel.saveScopeAllArticles}</span>
                            <span className={styles.scopeOptionHint}>{t.layoutPanel.saveScopeAllArticlesHint}</span>
                          </label>
                          <label className={styles.scopeOption}>
                            <input
                              type="radio"
                              name="saveScope"
                              value="catalog"
                              checked={saveScope === "catalog"}
                              onChange={() => setSaveScope("catalog")}
                              disabled={previewMode || !canModify}
                            />
                            <span className={styles.scopeOptionLabel}>{t.layoutPanel.saveScopeCatalog}</span>
                            <span className={styles.scopeOptionHint}>{t.layoutPanel.saveScopeCatalogHint}</span>
                          </label>
                        </fieldset>
                        );
                      })()}

                      {/* Bannery stanu — informują admin'a o source konfiguracji.
                          Trzy mutually-exclusive stany:
                            1. inherits_default -> używa template_default bez customu
                            2. modified -> ma własną konfigurację różną od defaulta
                            3. no_default -> brak template_default (warning) */}
                      {!hasCustomConfig && layoutSource === "template_default" && (
                        <FormStatus variant="info">
                          {t.layoutPanel.inheritsDefaultBannerPrefix}{" "}
                          <strong>{t.layoutPanel.inheritsDefaultBannerBold}</strong>
                          {t.layoutPanel.inheritsDefaultBannerSuffix}
                        </FormStatus>
                      )}
                      {hasCustomConfig && defaultTemplateExists && (
                        <FormStatus variant="warning">
                          {t.layoutPanel.modifiedFromDefaultBannerPrefix}{" "}
                          <strong>{t.layoutPanel.modifiedFromDefaultBannerBold}</strong>
                          {t.layoutPanel.modifiedFromDefaultBannerSuffix}
                        </FormStatus>
                      )}
                      {!hasCustomConfig && layoutSource === "fallback" && !defaultTemplateExists && availableLayouts.length > 0 && (
                        <FormStatus variant="warning">
                          <strong>{t.layoutPanel.noDefaultBannerBold}</strong>
                          {t.layoutPanel.noDefaultBannerSuffix}
                        </FormStatus>
                      )}

                      {/* Akcje template_default — "Zapisz/Nadpisz domyślny"
                          + "Przywróć ustawienia domyślne". Oba disabled gdy
                          podstrona używa template_default bez zmian
                          (`!hasCustomConfig`) — nie ma czego zapisywać
                          (równa się domyślnemu) ani przywracać. */}
                      <div className={styles.defaultActionsRow}>
                        <button
                          type="button"
                          className={styles.saveDefaultBtn}
                          onClick={handleSaveAsDefault}
                          disabled={availableLayouts.length === 0 || !selectedMeta || !hasCustomConfig || previewMode || !canModify}
                          title={
                            !canModify
                              ? t.layoutPanel.editLockedNoPermissionTooltip
                              : previewMode
                              ? t.layoutPanel.editLockedInPreviewTooltip
                              : availableLayouts.length === 0
                              ? t.layoutPanel.saveDefaultDisabledNoLayouts
                              : !hasCustomConfig
                              ? t.layoutPanel.saveDefaultDisabledNoChanges
                              : defaultTemplateExists
                              ? t.layoutPanel.saveDefaultTooltipOverwrite
                              : t.layoutPanel.saveDefaultTooltipFresh
                          }
                        >
                          <Icon name="floppy-disk" />
                          <span>
                            {defaultTemplateExists ? t.layoutPanel.overrideDefaultLabel : t.layoutPanel.saveAsDefaultLabel}
                          </span>
                        </button>

                        {/* Reset — pełny wipe wpisów dla id_catalog (3 tabele).
                            Disabled gdy strona już używa template_default
                            i nie ma żadnego customu. */}
                        {defaultTemplateExists && (
                          <button
                            type="button"
                            className={styles.resetDefaultsBtn}
                            onClick={handleResetToDefaults}
                            disabled={!hasCustomConfig || previewMode || !canModify}
                            title={
                              !canModify
                                ? t.layoutPanel.editLockedNoPermissionTooltip
                                : previewMode
                                ? t.layoutPanel.editLockedInPreviewTooltip
                                : hasCustomConfig
                                ? t.layoutPanel.resetToDefaultsTooltip
                                : t.layoutPanel.resetToDefaultsDisabledNoCustom
                            }
                          >
                            <Icon name="arrow-counter-clockwise" />
                            <span>{t.layoutPanel.resetToDefaultsLabel}</span>
                          </button>
                        )}

                        {/* Toggle preview układu domyślnego — widoczny gdy
                            `previewData` jest dostępne (backend zwrócił template
                            default). Button NIE blokuje się w preview mode
                            (musi zostać klikalny żeby wyłączyć preview). */}
                        {previewData !== null && (
                          <button
                            type="button"
                            className={styles.previewToggleBtn}
                            onClick={togglePreviewMode}
                            title={
                              previewMode
                                ? t.layoutPanel.previewModeExitTooltip
                                : t.layoutPanel.previewModeShowTooltip
                            }
                          >
                            <Icon name={previewMode ? "x" : "eye"} />
                            <span>
                              {previewMode
                                ? t.layoutPanel.previewModeExitLabel
                                : t.layoutPanel.previewModeShowLabel}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            </div>
            )}
          </section>

          {/* KOLUMNA 2: Biblioteka okien — wszystkie okna domeny+języka MINUS
              te już dodane na tę stronę. Source: `unpublishedWindows` z
              `AdminContext` (PublicLayout fetcha z `include_published=1` i
              filtruje out current page'a). */}
          <section className={`${styles.column} ${styles.columnWide}`}>
            <div className={styles.colHeader}>
              {/* Tytuł + chevron jako pojedynczy clickable obszar — `<button>`
                  rozwija/zwija sekcję. Cały zestaw "title+licznik" jest
                  klikalny żeby trafienie nie wymagało celowania w mały
                  chevron (Fitts's law). */}
              <button
                type="button"
                className={styles.sectionToggle}
                onClick={() => setLibraryCollapsed(!libraryCollapsed)}
                aria-expanded={!libraryCollapsed}
                aria-controls="cms-library-body"
                title={
                  libraryCollapsed
                    ? t.layoutPanel.sectionExpandTitle
                    : t.layoutPanel.sectionCollapseTitle
                }
              >
                <Icon
                  name={libraryCollapsed ? "caret-down" : "caret-up"}
                  className={styles.sectionToggleIcon}
                />
                <h3 className={styles.colTitle}>Biblioteka okien</h3>
                {/* Licznik widoczny zwłaszcza gdy collapsed — admin wie ile
                    okien jest dostępnych bez rozwijania. */}
                <span className={styles.sectionCount}>
                  ({filteredUnpublishedWindows.length}
                  {filteredUnpublishedWindows.length !== unpublishedWindows.length &&
                    ` / ${unpublishedWindows.length}`})
                </span>
              </button>
              {/* "Dodaj" -> Imperial Administrator windows_manager w trybie add (nowa
                  karta). Pattern URL zgodny z `buildAdminLinksForWindow` w PHP,
                  tylko bez `id` i z `action=add`. `s_lang` (admin UI lang)
                  hardcoded 'pl' — same default jak backend; `sa_lang` (managed
                  content lang) z locale.

                  **NIE używamy `withContinue`** — dokleja `&continue=<URL>`
                  z `window.location.href`, powodując hydration mismatch (SSR
                  renderuje bez param'a, client dokleja po hydratacji). Dla
                  `target="_blank"` continue jest bezużyteczny (nowa karta nie
                  wraca do original tab — user zamyka nową kartę po zakończeniu). */}
              <a
                href={buildIadminFullUrl(adminPanelUrl, iadminUrl.addWindow({ locale }))}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.addBtn}
                title={t.layoutPanel.addWindowTitle}
              >
                <Icon name="plus" />
                <span>{t.layoutPanel.addButtonLabel}</span>
              </a>
            </div>

            {/* Body sekcji — ukryte gdy collapsed. Header (z toggle, licznikiem,
                "Dodaj") zostaje zawsze widoczny żeby admin mógł rozwinąć lub
                otworzyć Imperial Administrator bez expand'a. */}
            {!libraryCollapsed && (
            <div id="cms-library-body" className={styles.libraryBody}>
            {/* Instrukcja dla admina — co zrobić z oknami z listy poniżej. */}
            <p className={styles.dropHint}>
              <Icon name="info" className={styles.dropHintIcon} />
              <span>{t.layoutPanel.libraryDropHint}</span>
            </p>

            {/* Toolbar: dropdown filter (typ komponentu) PO LEWEJ + search input PO PRAWEJ. */}
            <div className={styles.filterBar}>
              <select
                className={styles.componentFilter}
                value={componentFilter}
                onChange={(e) => setComponentFilter(e.target.value)}
                title={t.layoutPanel.componentFilterTitle}
                aria-label={t.layoutPanel.componentFilterAriaLabel}
              >
                <option value="">{t.layoutPanel.allTypesOption}</option>
                {componentTypeOptions.map((opt) => (
                  <option key={opt.symbol} value={opt.symbol}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div className={styles.searchWrap}>
                <Icon name="magnifying-glass" className={styles.searchIcon} />
                <input
                  type="search"
                  className={styles.search}
                  placeholder={t.layoutPanel.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <ul className={styles.windowGrid}>
              {filteredUnpublishedWindows.map((w) => {
                /* Czy choć jeden z aktualnych areas w wybranym layoucie jest dla tego okna? */
                /* Chrome areas (header/footer) są ZAWSZE dostępne niezależnie
                   od page-layout, więc dorzucamy je do dostępnej puli. Bez tego
                   okno z allowedAreas typu "headerCol1" + "footerRight" było
                   fałszywie disabled w layoutach gdzie body nie miało takich obszarów. */
                const availableAreaSymbols: string[] = [
                  ...(selectedMeta?.areas.map((a) => a.area_key) ?? []),
                  ...CHROME_AREAS,
                ];
                const compatibleAreas = w.allowedAreas.filter((a) =>
                  availableAreaSymbols.includes(a),
                );
                const compatible = compatibleAreas.length > 0;
                const componentLabel = pickLocaleLabel(w.componentName, w.componentSymbol);
                const isExpanded = selectedWindowId === w.id;
                const boxClass = [
                  styles.windowBox,
                  !compatible && styles.windowBoxDisabled,
                  isExpanded && styles.windowBoxExpanded,
                ].filter(Boolean).join(" ");

                /* Action handlers — `e.stopPropagation()` w środku, żeby
                   click nie propagował do `<li>` i nie collapse'ował tile'a.
                   Usuń jest tymczasowo placeholder'em (toast.error) — endpoint
                   DELETE /cms/windows/{id} podłączymy w Fazie F.2+. */
                const handleDelete = async (e: React.MouseEvent) => {
                  e.stopPropagation();
                  const ok = await confirmDialog({
                    title: t.layoutPanel.deleteWindowDialogTitle,
                    message: t.layoutPanel.deleteWindowDialogMessage(w.name),
                    variant: "danger",
                    confirmLabel: t.layoutPanel.deleteWindowConfirmLabel,
                    cancelLabel: t.layoutPanel.cancelLabel,
                  });
                  if (!ok) return;
                  /* Server Action — DELETE /cms/windows/{id}. FK CASCADE w DB
                     auto-czyści wpisy w `imp_cms_catalog_window`. revalidatePath
                     w action'ie odświeży `unpublishedWindows` z context'u. */
                  const result = await withPendingRequest(() =>
                    deleteWindowAction({ id: w.id, lang: locale, slug: currentSlug }),
                  );
                  if (result.ok) {
                    toast.success(t.layoutPanel.deleteWindowSuccessToast(w.name));
                    setSelectedWindowId(null);
                  } else {
                    toast.error(t.layoutPanel.deleteWindowErrorToast(w.name, result.error ?? t.contextToasts.unknownError));
                  }
                };

                /* Linki do IADMIN z backendu (`buildAdminLinksForWindow` w
                   windows_manager.php). `contentUrl=null` gdy komponent nie ma
                   `_admin.php` — button "Zarządzaj treścią" blocked z tooltipem.

                   NIE używamy `withContinue` — linki otwieramy w `target="_blank"`,
                   nowa karta nie wraca do oryginalnej po close, a doklejanie
                   `continue=window.location.href` w client-side render'ze
                   powodowałoby hydration mismatch (SSR zwraca URL bez param'a,
                   client dokleja po hydratacji). */
                const configHref = buildIadminFullUrl(adminPanelUrl, w.adminLinks.configUrl);
                const contentHref = w.adminLinks.contentUrl
                  ? buildIadminFullUrl(adminPanelUrl, w.adminLinks.contentUrl)
                  : null;
                const contentBlockedReason = w.adminLinks.contentBlockedReason;

                return (
                  <li
                    key={w.id}
                    className={boxClass}
                    draggable={compatible}
                    onDragStart={(e) => {
                      if (!compatible) {
                        e.preventDefault();
                        return;
                      }
                      /* Drag === implicit collapse. User chce przeciągać,
                         nie czytać szczegółów — wracamy do compact state. */
                      setSelectedWindowId(null);
                      handleWindowDragStart(e, w.id);
                    }}
                    onDragEnd={handleWindowDragEnd}
                    onClick={() =>
                      setSelectedWindowId((curr) => (curr === w.id ? null : w.id))
                    }
                    title={
                      isExpanded
                        ? t.layoutPanel.tileExpandedTooltip
                        : compatible
                        ? t.layoutPanel.tileCompatibleTooltip(w.name, componentLabel)
                        : t.layoutPanel.tileIncompatibleTooltip(w.name, componentLabel)
                    }
                  >
                    {isExpanded ? (
                      <>
                        <div className={styles.expandedHeader}>
                          <Icon name="squares-four" className={styles.expandedIcon} />
                          <div className={styles.expandedTitles}>
                            <h4 className={styles.expandedName}>
                              {w.name}
                              {/* Info icon z notatkami admina (`imp_cms_window.text`).
                                  Wypełnione -> klikalna ikona z tooltipem (HTML title).
                                  Null -> ikona zablokowana z hint'em "uzupełnij w IADMIN". */}
                              {w.adminNotes && w.adminNotes.trim() !== "" ? (
                                <button
                                  type="button"
                                  className={styles.expandedNoteBtn}
                                  onClick={(e) => e.stopPropagation()}
                                  title={w.adminNotes}
                                  aria-label={t.layoutPanel.notesAriaLabel}
                                >
                                  <Icon name="info" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className={`${styles.expandedNoteBtn} ${styles.expandedNoteBtnDisabled}`}
                                  onClick={(e) => e.stopPropagation()}
                                  disabled
                                  title={t.layoutPanel.noNotesTitle}
                                  aria-label={t.layoutPanel.noNotesAriaLabel}
                                >
                                  <Icon name="info" />
                                </button>
                              )}
                            </h4>
                            <p className={styles.expandedComponent}>{componentLabel}</p>
                          </div>
                          <span className={styles.expandedHint}>
                            {t.layoutPanel.tileCollapseHint}
                          </span>
                        </div>

                        <div className={styles.expandedActions}>
                          <a
                            href={configHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.detailActionBtn}
                            onClick={(e) => e.stopPropagation()}
                            title={t.layoutPanel.openConfigTitle}
                          >
                            <Icon name="gear" />
                            <span>{t.layoutPanel.actionWindowConfig}</span>
                          </a>

                          {contentHref !== null ? (
                            <a
                              href={contentHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.detailActionBtn}
                              onClick={(e) => e.stopPropagation()}
                              title={t.layoutPanel.openContentEditorTitle}
                            >
                              <Icon name="pencil-simple" />
                              <span>{t.layoutPanel.actionManageContent}</span>
                            </a>
                          ) : (
                            <button
                              type="button"
                              className={`${styles.detailActionBtn} ${styles.detailActionBtnDisabled}`}
                              onClick={(e) => e.stopPropagation()}
                              disabled
                              title={contentBlockedReason ?? t.layoutPanel.contentEditorBlockedDefault}
                            >
                              <Icon name="pencil-simple" />
                              <span>{t.layoutPanel.actionManageContent}</span>
                            </button>
                          )}

                          <button
                            type="button"
                            className={`${styles.detailActionBtn} ${styles.detailActionBtnDanger}`}
                            onClick={handleDelete}
                            disabled={!canDelete}
                            title={
                              !canDelete
                                ? t.layoutPanel.deleteWindowNoPermissionTooltip
                                : t.layoutPanel.deleteWindowTitle
                            }
                          >
                            <Icon name="trash" />
                            <span>{t.layoutPanel.actionDeleteWindow}</span>
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <Icon name="squares-four" className={styles.windowIcon} />
                        <span className={styles.windowName}>{w.name}</span>
                      </>
                    )}
                  </li>
                );
              })}
              {unpublishedWindows.length === 0 && (
                <li className={styles.emptyHint}>
                  {t.layoutPanel.emptyLibraryMessage}
                </li>
              )}
              {unpublishedWindows.length > 0 && filteredUnpublishedWindows.length === 0 && (
                <li className={styles.emptyHint}>
                  {t.layoutPanel.noWindowsMatchingFilters}
                </li>
              )}
            </ul>
            </div>
            )}
          </section>

          {/* SEKCJA 3: Układ strony — mini-mapa wybranego layoutu pod oknami
              nieopublikowanymi. Każdy obszar to drop target dla okien
              przeciąganych z listy powyżej (lub między obszarami). Widoczne
              tylko w panelu admina — NIE renderuje się na realnej stronie.

              **Draft mode (Faza G.11):** zmiany w mini-mapie NIE są
              natychmiast persistowane — admin akumuluje je lokalnie i klika
              "Zatwierdź zmiany" żeby zapisać do backendu + pokazać visitor'owi.
              `LayoutMap` zarządza własnym draft state'em. */}
          <section className={`${styles.column} ${styles.columnWide}`}>
            <button
              type="button"
              className={styles.sectionToggle}
              onClick={() => setPageLayoutCollapsed(!pageLayoutCollapsed)}
              aria-expanded={!pageLayoutCollapsed}
              aria-controls="cms-page-layout-body"
              title={
                pageLayoutCollapsed
                  ? t.layoutPanel.sectionExpandTitle
                  : t.layoutPanel.sectionCollapseTitle
              }
            >
              <Icon
                name={pageLayoutCollapsed ? "caret-down" : "caret-up"}
                className={styles.sectionToggleIcon}
              />
              <h3 className={styles.colTitle}>{t.layoutPanel.pageLayoutHeading}</h3>
            </button>
            {!pageLayoutCollapsed && (
            <div id="cms-page-layout-body" className={styles.libraryBody}>
              <p className={styles.colHint}>
                {t.layoutPanel.dragWindowFromListHint}{" "}
                <strong>{t.layoutPanel.commitChangesEmphasis}</strong>.
              </p>
              <LayoutMap
                layoutMeta={selectedMeta}
                committedState={windowsState}
                committedDisabledAreas={disabledAreas}
                pageWindows={pageWindows}
                unpublishedWindows={unpublishedWindows}
                onCommit={commitWindowsState}
                onCommitAreaStates={commitAreaStateChanges}
                onDraftPublishedIdsChange={setDraftPublishedIds}
              />
            </div>
            )}
          </section>
        </div>

        <div className={styles.previewHint}>
          <span className={styles.previewHintText}>
            Poniżej widzisz podgląd swojej strony tak, jak zobaczy ją odwiedzający
          </span>
          <Icon name="caret-down" className={styles.previewHintArrow} />
        </div>
      </div>
    </aside>
  );
}

/* ─── LayoutMap — mini-mapa układu strony z drop targets (draft mode) ──── */

/**
 * Mini-mapa odwzorowująca wizualnie stronę dla odwiedzającego:
 *   - **Nagłówek** (4 obszary: `headerCol1..4`) — 4 kolumny, wyrównane do prawej
 *   - **Body** — N kolumn zgodnie z layout metadata (1 dla LSingleColumn,
 *     2 dla LTwoCols*, 3 dla LThreeCols, ...). Body areas są filtrowane
 *     z `selectedMeta.areas` przez wykluczenie `CHROME_AREAS` (backend
 *     zwraca chrome areas w `imp_cms_layout_areas` razem z body — frontend
 *     traktuje je osobno bo renderują je `HDefault`/`FDefault`, nie L*).
 *   - **Stopka** — 3 kolumny (`footerLeft` / `footerMiddle` / `footerRight`)
 *
 * **Draft mode:** wszystkie zmiany trzymane w lokalnym `draftState` (useState).
 * Strona realna renderuje się z `committedState` (z context'u, persistowany).
 * Admin klika "Zatwierdź zmiany" -> `onCommit(draftState)` (persist + visitor'owi
 * widoczne). "Cofnij" -> reset draft do committed. Dirty detection przez
 * porównanie areas/windowsIds.
 *
 * Każdy obszar to drop target — okno z panelu (`DRAG_FROM_PANEL`) lub
 * z innego obszaru (`DRAG_FROM_AREA`). Walidacja `allowedAreas` w `onDrop`:
 * invalid -> flash 600ms + no-op.
 *
 * Renderuje się TYLKO w panelu admina — strona realna pozostaje nietknięta
 * aż do commit'a.
 */
function LayoutMap({
  layoutMeta,
  committedState,
  committedDisabledAreas,
  pageWindows,
  unpublishedWindows,
  onCommit,
  onCommitAreaStates,
  onDraftPublishedIdsChange,
}: LayoutMapProps) {
  const { t, previewMode, canModify, cmsConfig } = useAdmin();
  /* Edit locked — user nie ma uprawnień do edycji (canModify=false) ALBO
     jest w preview mode. Oba prowadzą do tego samego — read-only UX. */
  const editLocked = previewMode || !canModify;
  /* Local saving lock — set URGENT na klik "Zatwierdź" + reset w `finally`
     po await onCommit. `commitWindowsState` zwraca teraz Promise który
     czeka na zakończenie pętli save (assignLayoutAction + saveAreaWindowsAction
     per area). Pewny reset niezależnie od success/fail. */
  const [isSaving, setIsSaving] = useState(false);

  /* Local draft state — kopia committedState'u. Sync'ujemy gdy committedState
     się zmieni z zewnątrz (np. revalidatePath po innym save), żeby user nie
     widział stale danych. Sync TYLKO gdy draft jest clean (`isDirty=false`) —
     unikamy nadpisywania zmian user'a w trakcie edycji. */
  const [draftState, setDraftState] = useState<WindowsLayoutState>(committedState);

  /* Draft area states — toggle eye-icon w nagłówku obszaru NIE persistuje
     natychmiast, akumuluje się tutaj. Commit razem z draftState w handleCommit. */
  const [draftDisabledAreas, setDraftDisabledAreas] = useState<AreaSymbol[]>(committedDisabledAreas);

  const isWindowsDirty = isStateDifferent(draftState, committedState);
  const isAreasDirty = isDisabledAreasDifferent(draftDisabledAreas, committedDisabledAreas);
  const isDirty = isWindowsDirty || isAreasDirty;

  useEffect(() => {
    if (!isDirty) {
      setDraftState(committedState);
      setDraftDisabledAreas(committedDisabledAreas);
    }
    /* When isDirty=true: nie nadpisujemy. User skończy edycję, zatwierdzi
       albo cofnie — wtedy isDirty=false i następna sync zadziała. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committedState, committedDisabledAreas]);

  /* Toggle obszaru w draft — wywoływane z LayoutMapArea button eye/eye-slash.
     Catalog area filtrowany w samym LayoutMapArea (toggle ukryty dla isBlocked). */
  const toggleAreaInDraft = (area: AreaSymbol) => {
    if (editLocked) return; //lock when no modify perm OR in preview mode
    setDraftDisabledAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area],
    );
  };

  /* Notyfikacja parent'a o aktualnych draft IDs — używane do filtrowania
     panelu "Biblioteka okien" żeby przeciągnięte okno znikło z listy od razu,
     nie dopiero po save'ie + revalidate. */
  useEffect(() => {
    if (!onDraftPublishedIdsChange) return;
    const ids = new Set<number>();
    for (const list of Object.values(draftState.publishedByArea)) {
      if (list) {
        for (const id of list) ids.add(id);
      }
    }
    onDraftPublishedIdsChange(ids);
  }, [draftState, onDraftPublishedIdsChange]);

  /* Local mutators — modyfikują tylko draftState. Jeden uniwersalny add'er
     obsługuje wszystkie cases: panel->area, area->area, within-area reorder.
     Filtrujemy windowId z KAŻDEGO obszaru (więc okno trafia tylko w jednym
     miejscu, niezależnie od pochodzenia), potem insert w target area na
     pozycji `insertIndex` (lub append na koniec gdy undefined). */
  const draftPlaceWindow = (
    windowId: number,
    area: AreaSymbol,
    insertIndex?: number,
  ) => {
    if (editLocked) return; //lock when no modify perm OR in preview mode
    setDraftState((prev) => {
      const cleaned: WindowsLayoutState["publishedByArea"] = {};
      for (const [a, ids] of Object.entries(prev.publishedByArea)) {
        cleaned[a as AreaSymbol] = (ids ?? []).filter((id) => id !== windowId);
      }
      const targetIds = [...(cleaned[area] ?? [])];
      const idx =
        insertIndex === undefined
          ? targetIds.length
          : Math.max(0, Math.min(insertIndex, targetIds.length));
      targetIds.splice(idx, 0, windowId);
      const next = { publishedByArea: { ...cleaned, [area]: targetIds } };
      return next;
    });
  };

  const draftRemoveWindow = (windowId: number, area: AreaSymbol) => {
    if (editLocked) return; //lock when no modify perm OR in preview mode
    setDraftState((prev) => {
      const next = {
        publishedByArea: {
          ...prev.publishedByArea,
          [area]: (prev.publishedByArea[area] ?? []).filter((id) => id !== windowId),
        },
      };
      return next;
    });
  };

  const handleCommit = async () => {
    if (editLocked) return; //lock when no modify perm OR preview mode (zob. editLocked above)
    /* Set isSaving=true URGENT (synchronicznie, przed await onCommit). Bez
       tego visual lock pojawia się ze sporym opóźnieniem (lub wcale) — React
       traktuje update jako urgent gdy fire'ujemy go w event handler'ze.

       Sekwencja: najpierw okna (zawiera assignLayoutAction PRZED save'ami),
       potem area states. Sequential żeby uniknąć race condition na
       revalidatePath. Try/finally gwarantuje reset isSaving. */
    setIsSaving(true);
    try {
      if (isWindowsDirty) {
        await onCommit(draftState);
      }
      if (isAreasDirty) {
        await onCommitAreaStates(draftDisabledAreas);
      }
    } finally {
      setIsSaving(false);
    }
    /* Po commit committedState zaktualizuje się z context'u przez useEffect
       (revalidatePath -> re-render -> nowy windowsState prop). Draft nie kasujemy
       ręcznie — sync zrobi to za nas. */
  };

  const handleCancel = () => {
    setDraftState(committedState);
    setDraftDisabledAreas(committedDisabledAreas);
  };

  if (!layoutMeta) {
    return (
      <div className={styles.mapEmpty}>
        {t.layoutPanel.mapEmptyPickLayout}
      </div>
    );
  }

  /* Segregacja body areas na sekcje wizualne odzwierciedlające layout
     visitor view:
       1. hero — opcjonalny split 2 cols × 2 rows NAD `main` (LSingleColumn
          home): `heroLarge` lewa kolumna (rowspan 2) + `heroSmallTop` /
          `heroSmallBottom` prawa kolumna (po 1 row).
       2. middle — sidebars + main/catalogContent (per-layout cols)
       3. `belowCatalog` — full-width row POD treścią katalogu (catalog
          layouts only).
     Dla layoutów nie używających danej sekcji, sekcja nie jest renderowana. */
  const HERO_AREA_KEYS = ["heroLarge", "heroSmallTop", "heroSmallBottom"];
  const allBodyAreas = layoutMeta.areas.filter(
    (a) => !(CHROME_AREAS as string[]).includes(a.area_key),
  );
  const heroAreas = allBodyAreas.filter((a) => HERO_AREA_KEYS.includes(a.area_key));
  const belowCatalogAreas = allBodyAreas.filter((a) => a.area_key === "belowCatalog");
  const middleAreas = allBodyAreas.filter(
    (a) => !HERO_AREA_KEYS.includes(a.area_key) && a.area_key !== "belowCatalog",
  );

  /* Lookup hero areas po area_key dla render'u 2×2 grid z rowspan na heroLarge. */
  const heroLargeMeta = heroAreas.find((a) => a.area_key === "heroLarge");
  const heroSmallTopMeta = heroAreas.find((a) => a.area_key === "heroSmallTop");
  const heroSmallBottomMeta = heroAreas.find((a) => a.area_key === "heroSmallBottom");

  const sharedAreaProps = {
    windowsState: draftState,
    pageWindows,
    unpublishedWindows,
    onPlaceWindow: draftPlaceWindow,
    onRemoveFromArea: draftRemoveWindow,
    draftDisabledAreas,
    onToggleArea: toggleAreaInDraft,
  };

  /* Helper — buduje label z dev-mode sufiksem (NODE_ENV=development pokazuje
     też technical area_key obok user-friendly nazwy). Pomaga adminowi/dev
     korelować visual z config'iem (np. który area podpiąć w `imp_cms_layout_areas`). */
  const labelFor = (areaMeta: { area_key: string; name: Record<string, string | undefined> }): string => {
    const friendly = pickLocaleLabel(areaMeta.name, areaMeta.area_key);
    if (process.env.NODE_ENV === "development" && friendly !== areaMeta.area_key) {
      return `${friendly} · ${areaMeta.area_key}`;
    }
    return friendly;
  };

  /* Wrap LayoutMapArea — wybiera czy obszar jest blocked (catalog content
     z `is_catalog_area=true` -> renderowany przez catalog component, nie drop
     target dla okien). */
  const renderArea = (areaMeta: {
    area_key: string;
    name: Record<string, string | undefined>;
    is_catalog_area: boolean;
  }) => (
    <LayoutMapArea
      key={areaMeta.area_key}
      area={areaMeta.area_key as AreaSymbol}
      label={labelFor(areaMeta)}
      isBlocked={areaMeta.is_catalog_area === true}
      {...sharedAreaProps}
    />
  );

  /* Lock panel podczas async save. `isSaving` jest LOCAL state (powyżej)
     set'owany URGENT na klik "Zatwierdź" (synchronously w handleCommit) i
     reset'owany przez useEffect[pendingRequests] gdy save się zakończy.
     Bez tego visual feedback nie pojawiał się — `pendingRequests > 0` z
     context'u był deferred przez startTransition w commitWindowsState. */
  return (
    <div
      className={`${styles.map}${isSaving ? ` ${styles.mapSaving}` : ""}`}
      aria-label={t.layoutPanel.miniMapAriaLabel}
      aria-busy={isSaving}
    >
      {/* Header section — 4 kolumny right-aligned. Mini-mapa odwzorowuje
          rzeczywisty układ z HDefault.tsx: 8-kolumnowy grid, w pierwszej
          połówce małe logo (taka sama source jak w visitor view), prawe 4
          kolumny to headerCol1..4 — wizualnie 50% szerokości po prawej. */}
      <div className={styles.mapSection}>
        <div className={styles.mapSectionLabel}>{t.layoutPanel.mapSectionHeader}</div>
        <div
          className={styles.mapRow}
          style={{ gridTemplateColumns: "repeat(8, 1fr)" }}
        >
          {/* Logo preview — taka sama source jak w HDefault (cmsConfig.logo_url
              z fallback'em /imperial-logo.svg). Wizualnie odwzorowuje co
              widzi visitor po lewej stronie nagłówka. */}
          <div
            className={styles.mapHeaderLogo}
            style={{ gridColumn: "1 / span 4" }}
            aria-hidden="true"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={cmsConfig?.logo_url || "/imperial-logo.svg"}
              alt=""
              className={styles.mapHeaderLogoImg}
            />
          </div>
          <LayoutMapArea
            area="headerCol1"
            label={
              process.env.NODE_ENV === "development"
                ? `${t.layoutPanel.mapAreaHeaderCol1} · headerCol1`
                : t.layoutPanel.mapAreaHeaderCol1
            }
            isBlocked={false}
            {...sharedAreaProps}
          />
          <LayoutMapArea
            area="headerCol2"
            label={
              process.env.NODE_ENV === "development"
                ? `${t.layoutPanel.mapAreaHeaderCol2} · headerCol2`
                : t.layoutPanel.mapAreaHeaderCol2
            }
            isBlocked={false}
            {...sharedAreaProps}
          />
          <LayoutMapArea
            area="headerCol3"
            label={
              process.env.NODE_ENV === "development"
                ? `${t.layoutPanel.mapAreaHeaderCol3} · headerCol3`
                : t.layoutPanel.mapAreaHeaderCol3
            }
            isBlocked={false}
            {...sharedAreaProps}
          />
          <LayoutMapArea
            area="headerCol4"
            label={
              process.env.NODE_ENV === "development"
                ? `${t.layoutPanel.mapAreaHeaderCol4} · headerCol4`
                : t.layoutPanel.mapAreaHeaderCol4
            }
            isBlocked={false}
            {...sharedAreaProps}
          />
        </div>
        {/* Drugi wiersz nagłówka — full-width area `headerBottom`. Mini-mapa
            odwzorowuje strukturę z HDefault.tsx (pod logo + 4 kolumnami +
            theme toggle). */}
        <div className={styles.mapRow} style={{ gridTemplateColumns: "1fr", marginTop: 4 }}>
          <LayoutMapArea
            area="headerBottom"
            label={
              process.env.NODE_ENV === "development"
                ? `${t.layoutPanel.mapAreaHeaderBottom} · headerBottom`
                : t.layoutPanel.mapAreaHeaderBottom
            }
            isBlocked={false}
            {...sharedAreaProps}
          />
        </div>
      </div>

      {/* Hero section — opcjonalny split 2 cols × 2 rows NAD main (LSingleColumn
          home): heroLarge po lewej (rowspan 2), heroSmallTop / heroSmallBottom
          po prawej w pionie. Match'uje proporcje z LSingleColumn.module.css
          (2fr/1fr × 1fr/1fr). Renderowane TYLKO gdy layout ma hero areas. */}
      {heroAreas.length > 0 && heroLargeMeta && heroSmallTopMeta && heroSmallBottomMeta && (
        <div className={styles.mapSection}>
          <div className={styles.mapSectionLabel}>{t.layoutPanel.mapSectionHero}</div>
          <div
            className={styles.mapRow}
            style={{
              display: "grid",
              gridTemplateColumns: "4fr 1fr",
              gridTemplateRows: "1fr 1fr",
              minHeight: 120,
            }}
          >
            <div style={{ gridColumn: 1, gridRow: "1 / 3", display: "grid" }}>
              {renderArea(heroLargeMeta)}
            </div>
            <div style={{ gridColumn: 2, gridRow: 1, display: "grid" }}>
              {renderArea(heroSmallTopMeta)}
            </div>
            <div style={{ gridColumn: 2, gridRow: 2, display: "grid" }}>
              {renderArea(heroSmallBottomMeta)}
            </div>
          </div>
        </div>
      )}

      {/* Middle (main body) — N kolumn wg wybranego layoutu. Wizualnie 2×
          wyższy niż header/footer (klasa `mapSectionBody`). */}
      <div className={`${styles.mapSection} ${styles.mapSectionBody}`}>
        <div className={styles.mapSectionLabel}>
          {t.layoutPanel.mapSectionBody(pickLocaleLabel(layoutMeta.name, layoutMeta.symbol))}
        </div>
        {middleAreas.length === 0 ? (
          <div className={styles.mapEmpty}>
            {t.layoutPanel.mapNoBodyAreas}
          </div>
        ) : (
          <div
            className={[
              styles.mapRow,
              styles.mapRowBody,
              layoutMeta.symbol === "LCatalogSingleColumn"
                ? styles.mapRowBodyCatalogSingle
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{
              gridTemplateColumns: bodyGridTemplate(middleAreas),
            }}
          >
            {middleAreas.map(renderArea)}
          </div>
        )}
      </div>

      {/* Below catalog — full-width row POD body. Renderowane TYLKO gdy
          layout ma ten obszar (catalog layouts). */}
      {belowCatalogAreas.length > 0 && (
        <div className={styles.mapSection}>
          <div className={styles.mapSectionLabel}>{t.layoutPanel.mapSectionBelowCatalog}</div>
          <div className={styles.mapRow} style={{ gridTemplateColumns: "1fr" }}>
            {belowCatalogAreas.map(renderArea)}
          </div>
        </div>
      )}

      {/* Footer section — analogicznie do header'a: logo po lewej (30%) +
          3 obszary chrome po prawej (70%). Mini-mapa odwzorowuje strukturę
          z FDefault.tsx (`.cols` grid 3fr/7fr z nested .areas 1fr 1fr 1fr). */}
      <div className={styles.mapSection}>
        <div className={styles.mapSectionLabel}>{t.layoutPanel.mapSectionFooter}</div>
        <div
          className={styles.mapRow}
          style={{ gridTemplateColumns: "3fr 7fr" }}
        >
          {/* Logo preview — taka sama source jak w FDefault (footer_logo_url
              -> logo_url -> /imperial-logo.svg). Wizualnie odwzorowuje co widzi
              visitor po lewej stronie stopki. */}
          <div className={styles.mapFooterLogo} aria-hidden="true">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={
                cmsConfig?.footer_logo_url ||
                cmsConfig?.logo_url ||
                "/imperial-logo.svg"
              }
              alt=""
              className={styles.mapFooterLogoImg}
            />
          </div>
          <div className={styles.mapFooterAreas}>
            <LayoutMapArea
              area="footerLeft"
              label={
                process.env.NODE_ENV === "development"
                  ? `${t.layoutPanel.mapAreaFooterLeft} · footerLeft`
                  : t.layoutPanel.mapAreaFooterLeft
              }
              isBlocked={false}
              {...sharedAreaProps}
            />
            <LayoutMapArea
              area="footerMiddle"
              label={
                process.env.NODE_ENV === "development"
                  ? `${t.layoutPanel.mapAreaFooterMiddle} · footerMiddle`
                  : t.layoutPanel.mapAreaFooterMiddle
              }
              isBlocked={false}
              {...sharedAreaProps}
            />
            <LayoutMapArea
              area="footerRight"
              label={
                process.env.NODE_ENV === "development"
                  ? `${t.layoutPanel.mapAreaFooterRight} · footerRight`
                  : t.layoutPanel.mapAreaFooterRight
              }
              isBlocked={false}
              {...sharedAreaProps}
            />
          </div>
        </div>
      </div>

      {/* Commit/cancel bar — widoczny tylko gdy draft != committed. */}
      {isDirty && (
        <div className={styles.mapCommitBar}>
          <Icon name="info" className={styles.mapCommitIcon} />
          <span className={styles.mapCommitMessage}>
            {t.layoutPanel.mapCommitMessage}{" "}
            <strong>{t.layoutPanel.commitChangesEmphasis}</strong>.
          </span>
          <button
            type="button"
            className={styles.mapCancelBtn}
            onClick={handleCancel}
            title={t.layoutPanel.discardChangesTitle}
          >
            {t.layoutPanel.mapCancelButton}
          </button>
          <button
            type="button"
            className={styles.mapCommitBtn}
            onClick={handleCommit}
            title={t.layoutPanel.commitChangesTitle}
          >
            <Icon name="check" />
            <span>{t.layoutPanel.mapCommitButton}</span>
          </button>
        </div>
      )}

    </div>
  );
}

type LayoutMapProps = {
  layoutMeta: CmsLayout | null;
  /** Persistowany stan z context'u — używany jako baseline do detekcji
      dirty + reset po cancel. Visitor view renderuje się z tego stanu. */
  committedState: WindowsLayoutState;
  /** Cascade-resolved lista disabled obszarów (committed source, z context.disabledAreas). */
  committedDisabledAreas: AreaSymbol[];
  pageWindows: UnpublishedWindow[];
  unpublishedWindows: UnpublishedWindow[];
  /** Callback persistujący draft windows state. Wywołany na klik "Zatwierdź zmiany". */
  onCommit: (next: WindowsLayoutState) => Promise<void>;
  /** Callback persistujący draft area states (diff vs committed). Wywołany na
      klik "Zatwierdź zmiany" SEKWENCYJNIE po `onCommit`. */
  onCommitAreaStates: (nextDisabledAreas: AreaSymbol[]) => Promise<void>;
  /** Notyfikacja parent'a o aktualnych ID okien w draftState (po każdej zmianie).
   *  Parent używa do filtrowania panelu "Biblioteka okien" — przeciągnięte okno
   *  znika z listy nieopublikowanych od razu, nie po save. */
  onDraftPublishedIdsChange?: (ids: Set<number>) => void;
};

/** Type import inline — `WindowsLayoutState` z `@/lib/windows/types`. */
type WindowsLayoutState = import("@/lib/windows/types").WindowsLayoutState;

/** Porównuje dwa state'y windows — true gdy się różnią (po area_key i liście id_window). */
/**
 * Porównanie 2 stanów layoutu — content-based, IGNORUJE struktury kluczy.
 *
 * Powód: backend (revalidatePath / Next.js ISR) bywa zwraca `publishedByArea`
 * z TYLKO niepustymi kluczami (np. `{footerLeft: [123]}`), a frontend draft
 * trzyma WSZYSTKIE klucze (`{headerCol1: [], footerLeft: [123], ...}`).
 * Wcześniejsza wersja sprawdzała `aKeys.length !== bKeys.length` -> fałszywie
 * raportowała `isDirty=true` po revalidacji, mimo że treść identyczna.
 * Skutek: przycisk "Zapisz" pojawiał się sam po cichu po jakimś czasie.
 *
 * Treat missing key === pusty array — to jest semantycznie równoważne dla
 * "obszar bez okien".
 */
/** Porównuje 2 listy disabled obszarów — true gdy się różnią (set semantics). */
function isDisabledAreasDifferent(a: AreaSymbol[], b: AreaSymbol[]): boolean {
  if (a.length !== b.length) return true;
  const setA = new Set(a);
  for (const k of b) if (!setA.has(k)) return true;
  return false;
}

function isStateDifferent(a: WindowsLayoutState, b: WindowsLayoutState): boolean {
  const allAreas = new Set<string>([
    ...Object.keys(a.publishedByArea),
    ...Object.keys(b.publishedByArea),
  ]);
  for (const k of allAreas) {
    const aIds = a.publishedByArea[k as AreaSymbol] ?? [];
    const bIds = b.publishedByArea[k as AreaSymbol] ?? [];
    if (aIds.length !== bIds.length) return true;
    for (let i = 0; i < aIds.length; i++) {
      if (aIds[i] !== bIds[i]) return true;
    }
  }
  return false;
}

/**
 * LayoutMapArea — pojedynczy obszar w mini-mapie. Renderuje label + listę
 * okien w tym obszarze + drop handlers.
 *
 * **Drag visual feedback:**
 *   - `draggedWindowId` (z AdminContext) -> pokazuje czy ten obszar
 *     jest **kompatybilny** (zielony outline) lub **niekompatybilny**
 *     (czerwony/wyszarzony) z aktualnie przeciąganym oknem.
 *   - Reguła compatibility: okno z `allowedAreas` musi zawierać `area`
 *     (lub `allowedAreas` puste = wszędzie OK).
 *
 * **Sortowanie w ramach obszaru** — między każdą parą okien (i przed
 * pierwszym + po ostatnim) renderowany jest cienki **drop indicator**.
 * Drop na indicator wstawia okno na pozycji `insertIndex`. Drop na samym
 * tle obszaru = append na końcu (fallback dla pustego obszaru).
 *
 * Każde okno = draggable mini-tile z X removing button.
 */
function LayoutMapArea({
  area,
  label,
  isBlocked = false,
  windowsState,
  pageWindows,
  unpublishedWindows,
  onPlaceWindow,
  onRemoveFromArea,
  draftDisabledAreas,
  onToggleArea,
}: LayoutMapAreaProps) {
  const {
    draggedWindowId,
    setDraggedWindowId,
    selectedWindowId,
    setSelectedWindowId,
    adminPanelUrl,
    locale,
    currentSlug,
    withPendingRequest,
    previewMode,
    canModify,
    canDelete,
    t,
  } = useAdmin();
  /* Edit locked — !canModify ALBO previewMode. Drag&drop + toggle obszaru
     wymagają `modify` permission. Read-only users widzą układ ale nie mogą
     edytować. */
  const editLocked = previewMode || !canModify;
  /* Draft state z LayoutMap'y (NIE z context'u) — toggle akumuluje się
     lokalnie, persist'uje się dopiero po klik "Zatwierdź zmiany". */
  const isAreaDisabled = draftDisabledAreas.includes(area);
  const [isDragOver, setIsDragOver] = useState(false);
  /* Insert hint — pokazuje gdzie aktualnie wstawi się okno gdy admin upuści.
     `idx` = index okna nad którym jest kursor, `position` = górna lub dolna
     połówka tego okna (split-target pattern, jak w Trello). null = brak hover
     na konkretnym oknie (kursor poza listą lub w pustym obszarze). */
  const [insertHint, setInsertHint] = useState<{ idx: number; position: "before" | "after" } | null>(null);

  const windowIds = windowsState.publishedByArea[area] ?? [];

  const findMeta = (id: number): UnpublishedWindow | undefined =>
    pageWindows.find((w) => w.id === id) ??
    unpublishedWindows.find((w) => w.id === id);

  /* Compatibility — gdy okno jest aktualnie przeciągane (`draggedWindowId !== null`),
     sprawdzamy czy ten obszar jest na liście `allowedAreas` tego okna.
     `null` = nic nie jest przeciągane (neutral state, no visual feedback). */
  const draggedMeta =
    draggedWindowId !== null ? findMeta(draggedWindowId) : undefined;

  const isCompatibleTarget: boolean | null = (() => {
    if (isBlocked) return false; //blocked area -> niedostępny jako drop target
    if (draggedWindowId === null) return null;
    if (!draggedMeta) return null; //unknown — fallback to no feedback
    if (!draggedMeta.allowedAreas || draggedMeta.allowedAreas.length === 0) {
      return true; //empty allowedAreas = window allowed everywhere
    }
    return draggedMeta.allowedAreas.includes(area);
  })();

  const handleAreaDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (editLocked) return; //lock drag — no modify perm OR preview mode
    if (isBlocked) return; //blocked area — totalne odrzucenie drop
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
    if (isCompatibleTarget === false) return; //blokujemy drop na niekompatybilnym
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!isDragOver) setIsDragOver(true);
  };

  const handleAreaDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    /* Browser fires dragleave też gdy kursor przechodzi nad dzieckiem
       (dragenter na child -> dragleave na rodzicu). Sprawdzamy czy faktycznie
       wyszliśmy poza ten element (relatedTarget != child). */
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    setIsDragOver(false);
    setInsertHint(null);
  };

  const handleAreaDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (editLocked) return; //lock drop — no modify perm OR preview mode
    const raw = e.dataTransfer.getData(DRAG_MIME);
    const payload = parseDragPayload(raw);
    setIsDragOver(false);
    setInsertHint(null);
    /* GLOBAL drag state cleanup — `setDraggedWindowId(null)` musi się stać na
       drop, nie tylko w `onDragEnd` source'a. Powód: po `onPlaceWindow(...)`
       state się aktualizuje, source `<li>` (okno w area A z którego ciągniemy)
       może zostać UNMOUNTED z DOM (okno przesunęło się do area B). Browser
       nie może już odpalić `onDragEnd` na nieistniejącym elemencie -> stary
       `draggedWindowId` lingeruje w stanie -> `isCompatibleTarget` w innych
       obszarach nadal mówi "drag active" -> tile'y świecą się dalej, dragged
       window pozostaje z niższym opacity. Forsujemy reset tutaj. */
    setDraggedWindowId(null);
    if (isCompatibleTarget === false) return;
    if (!payload) return;
    e.preventDefault();
    /* Insert index z hint'a (set'owanego przez window-level dragover) lub
       append na końcu gdy kursor nie był na konkretnym oknie (pusty obszar
       / poniżej ostatniego okna). */
    const insertIdx =
      insertHint === null
        ? undefined
        : insertHint.position === "after"
        ? insertHint.idx + 1
        : insertHint.idx;
    onPlaceWindow(payload.windowId, area, insertIdx);
  };

  /* Cursor split detection — Y kursora vs środek hover'owanego okna:
       - górna połówka -> insert PRZED tym oknem (`before`)
       - dolna połówka -> insert PO tym oknie (`after`)
     Daje natywne sortable list behavior bez osobnych "between" drop zone'ów. */
  const handleWindowDragOver = (e: React.DragEvent<HTMLLIElement>, idx: number) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
    if (isCompatibleTarget === false) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const position: "before" | "after" = e.clientY < midpoint ? "before" : "after";
    if (insertHint?.idx !== idx || insertHint?.position !== position) {
      setInsertHint({ idx, position });
    }
  };

  const cls = [
    styles.mapArea,
    isBlocked ? styles.mapAreaBlocked : "",
    !isBlocked && isAreaDisabled ? styles.mapAreaDisabled : "",
    !isBlocked && !isAreaDisabled && isDragOver ? styles.mapAreaDragOver : "",
    !isBlocked && !isAreaDisabled && isCompatibleTarget === true ? styles.mapAreaCompatible : "",
    !isBlocked && !isAreaDisabled && isCompatibleTarget === false ? styles.mapAreaIncompatible : "",
  ]
    .filter(Boolean)
    .join(" ");

  /* Toggle enabled/disabled w DRAFT — TYLKO dla non-blocked area (catalog
     content `is_catalog_area=1` zostaje zawsze widoczne, backend by odrzucił
     disable). Akumuluje się lokalnie, commit przyciskiem "Zatwierdź zmiany".
     Disabled w preview mode (lock edycji). */
  const toggleButton = isBlocked ? null : (
    <button
      type="button"
      className={[
        styles.mapAreaToggle,
        isAreaDisabled ? styles.mapAreaToggleDisabled : "",
      ].filter(Boolean).join(" ")}
      onClick={(e) => {
        e.stopPropagation();
        onToggleArea(area);
      }}
      disabled={editLocked}
      aria-label={
        isAreaDisabled
          ? t.layoutPanel.mapAreaEnableAria(label)
          : t.layoutPanel.mapAreaDisableAria(label)
      }
      title={
        !canModify
          ? t.layoutPanel.editLockedNoPermissionTooltip
          : previewMode
          ? t.layoutPanel.editLockedInPreviewTooltip
          : isAreaDisabled
          ? t.layoutPanel.mapAreaEnableAria(label)
          : t.layoutPanel.mapAreaDisableAria(label)
      }
    >
      <Icon name={isAreaDisabled ? "eye-slash" : "eye"} size={14} />
    </button>
  );

  /* Blocked area (catalog content) — renderuje tylko label + info, BEZ drop
     handlers, BEZ window list. Sygnalizujemy że treść tu przychodzi z innego
     źródła (catalog component), nie z drag&drop okien. */
  if (isBlocked) {
    return (
      <div className={cls} aria-label={t.layoutPanel.areaUnavailableAria(label)}>
        <div className={styles.mapAreaLabel}>{label}</div>
        <div className={styles.mapAreaBlockedHint}>
          <Icon name="lock" className={styles.mapAreaBlockedIcon} />
          <span>{t.layoutPanel.mapAreaBlockedHint}</span>
        </div>
      </div>
    );
  }

  /* Renderowanie:
     - Active area: pełna interaktywność (drag&drop, sortowanie)
     - Disabled area w draft: ten sam markup, ale wizualnie wyszarzony
       (`mapAreaDisabled` opacity 0.55) + drop handlers wyłączone żeby
       admin nie mógł dropować do strefy ktorą zaraz wyłącza. Okna
       pozostają widoczne (lista z opacity) — admin widzi co kryje. */
  const dropHandlers = isAreaDisabled
    ? {}
    : {
        onDragOver: handleAreaDragOver,
        onDragLeave: handleAreaDragLeave,
        onDrop: handleAreaDrop,
      };

  return (
    <div className={cls} {...dropHandlers}>
      <div className={styles.mapAreaHeader}>
        <div className={styles.mapAreaLabel}>{label}</div>
        {toggleButton}
      </div>

      {isAreaDisabled && (
        <div className={styles.mapAreaDisabledHint}>
          <Icon name="eye-slash" size={12} />
          <span>{t.layoutPanel.mapAreaDisabledHint}</span>
        </div>
      )}

      {windowIds.length === 0 ? (
        <div className={styles.mapAreaEmpty}>
          {isCompatibleTarget === false
            ? t.layoutPanel.mapAreaEmptyIncompatible
            : t.layoutPanel.mapAreaEmptyDropHere}
        </div>
      ) : (
        <ul className={styles.mapAreaWindows}>
          {windowIds.map((id, idx) => {
            const meta = findMeta(id);
            const name = meta?.name ?? `#${id}`;
            const componentLabel = meta
              ? pickLocaleLabel(meta.componentName, meta.componentSymbol)
              : `#${id}`;
            const isBeingDragged = draggedWindowId === id;
            const isExpanded = selectedWindowId === id;
            const showLineBefore =
              insertHint?.idx === idx && insertHint.position === "before";
            const showLineAfter =
              insertHint?.idx === idx && insertHint.position === "after";

            /* Per-window action handlers. `stopPropagation` żeby klik na akcji
               NIE zwijał tile'a (root <li> ma onClick toggle expand). */
            const handleRemoveFromArea = (e: React.MouseEvent) => {
              e.stopPropagation();
              onRemoveFromArea(id, area);
              setSelectedWindowId(null); //implicit collapse po akcji
            };
            const handleDelete = async (e: React.MouseEvent) => {
              e.stopPropagation();
              const ok = await confirmDialog({
                title: t.layoutPanel.deleteWindowDialogTitle,
                message: t.layoutPanel.deleteWindowDialogMessage(name),
                variant: "danger",
                confirmLabel: t.layoutPanel.deleteWindowConfirmLabel,
                cancelLabel: t.layoutPanel.cancelLabel,
              });
              if (!ok) return;
              /* Server Action — DELETE /cms/windows/{id}. CASCADE FK usuwa też
                 wszystkie wpisy z `imp_cms_catalog_window` (cross-page). */
              const result = await withPendingRequest(() =>
                deleteWindowAction({ id, lang: locale, slug: currentSlug }),
              );
              if (result.ok) {
                toast.success(t.layoutPanel.deleteWindowSuccessToast(name));
                setSelectedWindowId(null);
              } else {
                toast.error(t.layoutPanel.deleteWindowErrorToast(name, result.error ?? t.contextToasts.unknownError));
              }
            };

            /* IADMIN linki — patrz `buildAdminLinksForWindow` w backend.
               `contentUrl=null` -> button blocked z tooltipem (powód z backendu).
               Bez `withContinue` — target="_blank" nie wraca do original tab,
               a client-side continue param powodowałby hydration mismatch. */
            const configHref = meta
              ? buildIadminFullUrl(adminPanelUrl, meta.adminLinks.configUrl)
              : adminPanelUrl;
            const contentHref =
              meta?.adminLinks.contentUrl != null
                ? buildIadminFullUrl(adminPanelUrl, meta.adminLinks.contentUrl)
                : null;
            const contentBlockedReason =
              meta?.adminLinks.contentBlockedReason ?? null;

            return (
              <li
                key={id}
                className={[
                  styles.mapWindow,
                  isBeingDragged ? styles.mapWindowDragging : "",
                  isExpanded ? styles.mapWindowExpanded : "",
                  showLineBefore ? styles.mapWindowInsertBefore : "",
                  showLineAfter ? styles.mapWindowInsertAfter : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                draggable
                onDragStart={(e) => {
                  /* Drag === implicit collapse (parity z panelem nieopublikowanych
                     okien — user chce przeciągać, nie czytać szczegółów). */
                  setSelectedWindowId(null);
                  const payload = JSON.stringify({
                    type: DRAG_FROM_AREA,
                    windowId: id,
                    fromArea: area,
                    fromIndex: windowIds.indexOf(id),
                  });
                  e.dataTransfer.setData(DRAG_MIME, payload);
                  e.dataTransfer.effectAllowed = "move";
                  setDraggedWindowId(id);
                }}
                onDragEnd={() => {
                  setDraggedWindowId(null);
                  setInsertHint(null);
                }}
                onDragOver={(e) => handleWindowDragOver(e, idx)}
                onClick={() =>
                  setSelectedWindowId((curr) => (curr === id ? null : id))
                }
                title={
                  isExpanded
                    ? t.layoutPanel.tileExpandedTooltip
                    : t.layoutPanel.mapWindowMoveTooltip(name, componentLabel)
                }
              >
                {isExpanded ? (
                  <>
                    <div className={styles.expandedHeader}>
                      <Icon name="squares-four" className={styles.expandedIcon} />
                      <div className={styles.expandedTitles}>
                        <h4 className={styles.expandedName}>
                          {name}
                          {/* Info icon z notatkami admina (`imp_cms_window.text`).
                              Wypełnione -> klikalna ikona z tooltipem; null ->
                              ikona zablokowana z hint'em "uzupełnij w IADMIN". */}
                          {meta?.adminNotes && meta.adminNotes.trim() !== "" ? (
                            <button
                              type="button"
                              className={styles.expandedNoteBtn}
                              onClick={(e) => e.stopPropagation()}
                              title={meta.adminNotes}
                              aria-label={t.layoutPanel.notesAriaLabel}
                            >
                              <Icon name="info" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={`${styles.expandedNoteBtn} ${styles.expandedNoteBtnDisabled}`}
                              onClick={(e) => e.stopPropagation()}
                              disabled
                              title={t.layoutPanel.noNotesTitle}
                              aria-label={t.layoutPanel.noNotesAriaLabel}
                            >
                              <Icon name="info" />
                            </button>
                          )}
                        </h4>
                        <p className={styles.expandedComponent}>{componentLabel}</p>
                      </div>
                      <span className={styles.expandedHint}>
                        {t.layoutPanel.tileCollapseHint}
                      </span>
                    </div>

                    <div className={styles.expandedActions}>
                      <a
                        href={configHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.detailActionBtn}
                        onClick={(e) => e.stopPropagation()}
                        title={t.layoutPanel.openConfigTitle}
                      >
                        <Icon name="gear" />
                        <span>{t.layoutPanel.actionWindowConfig}</span>
                      </a>

                      {contentHref !== null ? (
                        <a
                          href={contentHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.detailActionBtn}
                          onClick={(e) => e.stopPropagation()}
                          title={t.layoutPanel.openContentEditorTitle}
                        >
                          <Icon name="pencil-simple" />
                          <span>{t.layoutPanel.actionManageContent}</span>
                        </a>
                      ) : (
                        <button
                          type="button"
                          className={`${styles.detailActionBtn} ${styles.detailActionBtnDisabled}`}
                          onClick={(e) => e.stopPropagation()}
                          disabled
                          title={contentBlockedReason ?? t.layoutPanel.contentEditorBlockedDefault}
                        >
                          <Icon name="pencil-simple" />
                          <span>{t.layoutPanel.actionManageContent}</span>
                        </button>
                      )}

                      <button
                        type="button"
                        className={styles.detailActionBtn}
                        onClick={handleRemoveFromArea}
                        disabled={editLocked}
                        title={
                          !canModify
                            ? t.layoutPanel.editLockedNoPermissionTooltip
                            : previewMode
                            ? t.layoutPanel.editLockedInPreviewTooltip
                            : t.layoutPanel.detachFromAreaTitle
                        }
                      >
                        <Icon name="arrow-counter-clockwise" />
                        <span>{t.layoutPanel.detachFromAreaLabel}</span>
                      </button>

                      <button
                        type="button"
                        className={`${styles.detailActionBtn} ${styles.detailActionBtnDanger}`}
                        onClick={handleDelete}
                        disabled={!canDelete}
                        title={
                          !canDelete
                            ? t.layoutPanel.deleteWindowNoPermissionTooltip
                            : t.layoutPanel.deleteWindowTitle
                        }
                      >
                        <Icon name="trash" />
                        <span>{t.layoutPanel.actionDeleteWindow}</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <Icon name="dots-six-vertical" className={styles.mapWindowDrag} />
                    <Icon name="squares-four" className={styles.mapWindowIcon} />
                    <span className={styles.mapWindowName}>{name}</span>
                    <button
                      type="button"
                      className={styles.mapWindowRemove}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveFromArea(id, area);
                      }}
                      aria-label={t.layoutPanel.detachWindowAria(name)}
                      title={t.layoutPanel.detachFromAreaShort}
                    >
                      <Icon name="x" />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

type LayoutMapAreaProps = {
  area: AreaSymbol;
  label: string;
  /** `true` gdy obszar jest "blocked" (np. catalog content area) — nie może
      być drop target dla okien, treść pochodzi z innego źródła. Backend
      sygnalizuje przez `imp_cms_layout_areas.is_catalog_area`. Default `false`. */
  isBlocked?: boolean;
  windowsState: import("@/lib/windows/types").WindowsLayoutState;
  pageWindows: UnpublishedWindow[];
  unpublishedWindows: UnpublishedWindow[];
  /** Umieszcza okno w `area` na pozycji `insertIndex` (lub append na końcu
      gdy undefined). Pokrywa wszystkie cases: panel->area, area->area,
      sortowanie wewnątrz obszaru. */
  onPlaceWindow: (windowId: number, area: AreaSymbol, insertIndex?: number) => void;
  onRemoveFromArea: (windowId: number, area: AreaSymbol) => void;
  /** Draft state disabled obszarów z LayoutMap'y. Toggle eye-icon zmienia
      tę listę lokalnie, persist'uje się dopiero po klik "Zatwierdź zmiany". */
  draftDisabledAreas: AreaSymbol[];
  /** Callback toggle obszaru w draft (z LayoutMap.toggleAreaInDraft). */
  onToggleArea: (area: AreaSymbol) => void;
};


/* Mock unpublished windows + ich allowedAreas żyje teraz w `lib/windows/data.ts`
   (single source of truth — używane też przez WindowArea do walidacji drop'u). */

/**
 * Oblicz `grid-template-columns` dla body row mini-mapy — proporcje
 * odzwierciedlają realne proporcje obszarów w visitor view:
 *   - `leftSidebar` / `rightSidebar` -> 30% (waga 3)
 *   - `main` / `catalogContent` -> 70% (waga 7)
 *   - inne obszary -> waga 1 (równe dzielenie reszty)
 *
 * **Przykłady wyników:**
 *   - LSingleColumn (main): `"7fr"` (jedna kolumna, pełna szerokość)
 *   - LTwoColsLeft (leftSidebar + main): `"3fr 7fr"` (30/70)
 *   - LTwoColsRight (main + rightSidebar): `"7fr 3fr"` (70/30)
 *   - LThreeCols (leftSidebar + main + rightSidebar): `"3fr 7fr 3fr"` (~23/54/23)
 *
 * Match'uje proporcje renderowane przez L* komponenty (CSS grid w
 * `LSingleColumn.module.css` etc. też używa 3fr/7fr).
 */
function bodyGridTemplate(areas: Array<{ area_key: string }>): string {
  if (areas.length === 0) return "1fr";
  return areas
    .map((a) => {
      if (a.area_key === "leftSidebar" || a.area_key === "rightSidebar") return "3fr";
      if (a.area_key === "main" || a.area_key === "catalogContent") return "7fr";
      return "1fr";
    })
    .join(" ");
}

/**
/**
 * Wybór labelu z multi-locale obiektu (`{pl: "...", en: "..."}`).
 *
 * Preferencja: `pl` (admin UI po polsku) -> pierwszy nie-pusty value -> `fallback`.
 * Backend zwraca puste/null component_name dla starych componentów bez locale
 * label'a — fallback do `componentSymbol` zachowuje UI usable.
 */
function pickLocaleLabel(
  locales: Record<string, string | undefined> | null | undefined,
  fallback: string,
): string {
  if (!locales) return fallback;
  if (typeof locales.pl === "string" && locales.pl.trim() !== "") return locales.pl;
  for (const v of Object.values(locales)) {
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return fallback;
}

/**
 * Hook: lokalny boolean state collapsed/expanded persistowany w localStorage.
 *
 * Initial state zawsze `false` (rozwinięty) żeby uniknąć SSR/hydration mismatch
 * — wartość z localStorage czytamy dopiero w `useEffect` post-mount. Setter
 * synchronizuje pamięć z localStorage. Pattern identyczny z `cms_dev_banner_hidden`
 * w AdminContext.
 *
 * @param storageKey klucz w localStorage (np. `cms_library_collapsed`)
 * @returns krotka `[collapsed, setCollapsed]` jak `useState<boolean>`
 */
function usePersistedCollapse(
  storageKey: string,
): [boolean, (collapsed: boolean) => void] {
  const [collapsed, setCollapsedLocal] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored === "1") setCollapsedLocal(true);
    } catch {
      /* SecurityError — localStorage nieprzystępne (Safari incognito itp.) */
    }
  }, [storageKey]);
  const setCollapsed = (next: boolean) => {
    setCollapsedLocal(next);
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey, next ? "1" : "0");
    } catch {
      /* SecurityError — ignore */
    }
  };
  return [collapsed, setCollapsed];
}
